import { createApi } from '@reduxjs/toolkit/dist/query/react';

import serverRoutes from '@app/utils/server-routes';

import {
  GPTGroupListObjectType,
  GptGroupMutationObjectType,
  GPTGroupObjectType,
  GPTGroupMetricListObjectType,
  GptGroupTestResultType,
  GptGroupManualCalcResult,
  GPTGroupListExportType,
} from '@app/interfaces/gpt-metric.type';

import { MetricObjType } from '@app/interfaces/pages-types/anatylics-metric.type';

import fileDownload from 'js-file-download';

import { baseQuery } from '../baseQuery';

export const gptMetricApi = createApi({
  reducerPath: 'gptMetricApi',
  tagTypes: ['gptGroupsList', 'gptGroupSingle'],
  baseQuery: baseQuery,
  endpoints: (build) => ({
    getGptGroupsWithMetrics: build.query<
      GPTGroupMetricListObjectType,
      { params: { project_id: string; gpt_group_id?: string } }
    >({
      query: ({ params }) => ({
        url: serverRoutes.gpt_group_metrics_bulk,
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.gpt_groups.map(({ gpt_group_id }) => ({
                type: 'gptGroupsList' as const,
                gpt_group_id,
              })),
              'gptGroupsList',
            ]
          : ['gptGroupsList'],
    }),
    getGptGroupsList: build.query<GPTGroupListObjectType, { project_id: string }>({
      query: (params) => ({
        url: serverRoutes.gpt_group_bulk,
        params,
      }),
    }),
    getGptGroupSingle: build.query<GPTGroupObjectType, { gpt_group_id: string }>({
      query: (params) => ({
        url: serverRoutes.gpt_group,
        params,
      }),
      providesTags: ['gptGroupSingle'],
    }),
    createGptGroup: build.mutation<GPTGroupObjectType, GptGroupMutationObjectType>({
      query: (body) => ({
        url: serverRoutes.gpt_group,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['gptGroupsList'],
    }),
    editGptGroup: build.mutation<
      GPTGroupObjectType,
      { body: Omit<GptGroupMutationObjectType, 'project_id'>; params: { gpt_group_id: string } }
    >({
      query: ({ body, params }) => ({
        url: serverRoutes.gpt_group,
        method: 'PUT',
        body,
        params,
      }),
      invalidatesTags: ['gptGroupSingle', 'gptGroupsList'],
    }),
    deleteGptGroup: build.mutation<GPTGroupObjectType, { gpt_group_id: string }>({
      query: (params) => ({
        url: serverRoutes.gpt_group,
        method: 'DELETE',
        params,
      }),
      invalidatesTags: ['gptGroupsList'],
    }),
    // IMPORT&EXPORT
    exportGptGroup: build.query<GPTGroupListExportType, { gpt_group_id: string }>({
      query: (params) => ({
        url: serverRoutes.gpt_group_export,
        params,
        responseHandler: async (response) => {
          const contentDisposition = response.headers.get('content-disposition');

          const buffer = await response.arrayBuffer();
          const text = new TextDecoder().decode(buffer);

          const filename =
            contentDisposition?.match(/filename="([^"]+)"/)?.[1] ||
            (JSON.parse(text) as GPTGroupListExportType).gpt_group.name + '.json';

          const blob = new Blob([buffer], { type: 'application/json' });
          fileDownload(blob, filename);
        },
        cache: 'no-cache',
      }),
    }),
    importGptGroup: build.mutation<GPTGroupObjectType, FormData>({
      query: (body) => ({
        url: serverRoutes.gpt_group_import,
        method: 'POST',
        body,
        // headers: { 'Content-Type': 'multipart/form-data' },
      }),
      invalidatesTags: ['gptGroupsList'],
    }),
    // IMPORT&EXPORT
    // SINGLE METRIC MUTATIONS
    createProjectMetricGroupForGpt: build.mutation<
      MetricObjType,
      {
        params: {
          project_id: string;
        };
        body: MetricObjType;
      }
    >({
      query: ({ body, params }) => ({
        url: serverRoutes.metricProject,
        method: 'POST',
        body,
        params,
      }),
      invalidatesTags: ['gptGroupsList'],
    }),
    editProjectMetricGroupForGpt: build.mutation<
      MetricObjType,
      {
        params: {
          project_id: string;
          metric_id: string;
        };
        body: MetricObjType;
      }
    >({
      query: ({ params, body }) => ({
        url: serverRoutes.metricProject,
        method: 'PUT',
        params,
        body,
      }),
      invalidatesTags: ['gptGroupsList'],
    }),
    deleteProjectMetricForGpt: build.mutation({
      query: (params: { project_id: string; metric_id: string }) => ({
        url: serverRoutes.projectMetric,
        method: 'DELETE',
        params,
        redirect: 'manual',
      }),

      invalidatesTags: ['gptGroupsList'],
    }),
    // SINGLE METRIC MUTATIONS
    // TEST CALC METRIC
    testCalcGptMetricsInGroup: build.query<
      GptGroupTestResultType,
      { gpt_group_id: string; record_id?: string }
    >({
      query: (params) => ({
        url: serverRoutes.gpt_group_metrics_calc_test,
        params,
      }),
    }),

    // TEST CALC METRIC
    // manual recalc gpt group
    recalculateGptGroupManual: build.query<
      GptGroupManualCalcResult,
      { gpt_group_id: string; record_id?: string }
    >({
      query: (params) => ({
        url: serverRoutes.gpt_group_metrics_manual_recalc,
        params,
        method: 'PUT',
      }),
    }),
    // manual recalc gpt group
  }),
});

export const {
  useGetGptGroupsListQuery,
  useLazyGetGptGroupSingleQuery,
  useCreateGptGroupMutation,
  useEditGptGroupMutation,
  useDeleteGptGroupMutation,
  useGetGptGroupsWithMetricsQuery,
  // SINGLE METRIC MUTATIONS
  useDeleteProjectMetricForGptMutation,
  useCreateProjectMetricGroupForGptMutation,
  useEditProjectMetricGroupForGptMutation,
  // TEST CALC METRIC
  useLazyTestCalcGptMetricsInGroupQuery,
  // manual recalc gpt group
  useLazyRecalculateGptGroupManualQuery,
  // IMPORT&EXPORT
  useLazyExportGptGroupQuery,
  useImportGptGroupMutation,
  //recalculate
} = gptMetricApi;
